/* ==========================================================
   Controlador Flutuante de Foco (inferior central)
   Substitui o banner fixo do topo (#focusSessionBanner).
   Dois estados: compacto (pill) e expandido (painel com acoes).

   Regras:
   - Mostra o tempo da sessao atual (nao o total do dia)
   - Auto-minimiza apos ~5s sem interacao
   - Escape sempre minimiza o painel expandido
   - Click fora do controlador minimiza

   O modulo e testavel em Node puro: nao toca em `document` no
   top-level; so dentro das funcoes explicitamente invocadas.
   ========================================================== */

export const FOCUS_CONTROLLER_BODY_CLASS = "focus-controller-visible";
export const FOCUS_CONTROLLER_EXPANDED_CLASS = "focus-controller-expanded";
export const FOCUS_CONTROLLER_AUTO_MINIMIZE_MS = 5000;
export const FOCUS_CONTROLLER_NO_EXPAND_VIEWS = [];

let deps = null;
let autoMinimizeTimer = null;
let configuredDoc = null;

/* ----- Helpers puros (testaveis) ----- */

export function focusControllerVisible(timer, focusOpen) {
  return Boolean(timer) && !focusOpen;
}

export function focusControllerShouldExpand(viewId, noExpandViews = FOCUS_CONTROLLER_NO_EXPAND_VIEWS) {
  return !noExpandViews.includes(viewId);
}

export function focusControllerTimerText(formatMs, elapsedMs) {
  return formatMs(elapsedMs);
}

/* ----- Helpers de DOM ----- */

function setText(doc, selector, value) {
  const el = doc.querySelector(selector);
  if (el) el.textContent = value;
}

function clearAutoMinimize() {
  if (autoMinimizeTimer !== null) {
    clearTimeout(autoMinimizeTimer);
    autoMinimizeTimer = null;
  }
}

function scheduleAutoMinimize() {
  clearAutoMinimize();
  if (!deps) return;
  autoMinimizeTimer = setTimeout(() => {
    collapseFocusController(deps.doc);
  }, FOCUS_CONTROLLER_AUTO_MINIMIZE_MS);
}

function collapseFocusController(doc) {
  clearAutoMinimize();
  const root = doc.querySelector("#focusController");
  const panel = doc.querySelector("#focusControllerPanel");
  const toggle = doc.querySelector("#focusControllerToggle");
  if (root) root.classList.remove(FOCUS_CONTROLLER_EXPANDED_CLASS);
  if (panel) panel.hidden = true;
  if (toggle) toggle.setAttribute("aria-expanded", "false");
}

function expandFocusController(doc) {
  const root = doc.querySelector("#focusController");
  const panel = doc.querySelector("#focusControllerPanel");
  const toggle = doc.querySelector("#focusControllerToggle");
  if (!root) return;
  root.classList.add(FOCUS_CONTROLLER_EXPANDED_CLASS);
  if (panel) panel.hidden = false;
  if (toggle) toggle.setAttribute("aria-expanded", "true");
  scheduleAutoMinimize();
}

/* ----- API publica ----- */

export function toggleFocusController() {
  if (!deps) return;
  const doc = deps.doc;
  const root = doc.querySelector("#focusController");
  if (!root) return;
  if (root.classList.contains(FOCUS_CONTROLLER_EXPANDED_CLASS)) {
    collapseFocusController(doc);
  } else if (focusControllerShouldExpand(deps.getViewId())) {
    expandFocusController(doc);
  }
}

export function renderFocusController() {
  if (!deps) return false;
  const doc = deps.doc;
  const root = doc.querySelector("#focusController");
  if (!root) return false;

  const timer = deps.getTimer();
  const focusOpen = deps.getFocusOpen();
  const visible = focusControllerVisible(timer, focusOpen);

  doc.body.classList.toggle(FOCUS_CONTROLLER_BODY_CLASS, visible);
  root.classList.toggle("hidden", !visible);
  root.setAttribute("aria-hidden", String(!visible));

  if (!visible) {
    collapseFocusController(doc);
    return false;
  }

  const subject = timer.subject;
  const running = Boolean(timer.running);
  const status = running ? "Sessão em andamento" : "Sessão pausada";

  const dot = doc.querySelector("#focusControllerDot");
  if (dot) dot.style.background = deps.getColors()[subject] || "var(--green)";

  setText(doc, "#focusControllerSubject", subject);
  setText(doc, "#focusControllerStatus", status);
  setText(doc, "#focusControllerTimer", deps.formatTimer(deps.getElapsedMs()));
  setText(doc, "#focusControllerPanelTimer", deps.formatTimer(deps.getElapsedMs()));

  const pauseBtn = doc.querySelector("#focusControllerPauseButton");
  if (pauseBtn) {
    const label = running ? "Pausar" : "Continuar";
    pauseBtn.textContent = label;
    pauseBtn.setAttribute("aria-label", label);
  }

  // Mantem o estado compacto apenas em secoes da lista de no-expand (atualmente vazia)
  if (!focusControllerShouldExpand(deps.getViewId())) {
    collapseFocusController(doc);
  }

  return true;
}

export function updateFocusControllerTimer() {
  if (!deps) return false;
  const doc = deps.doc;
  const root = doc.querySelector("#focusController");
  if (!root || root.classList.contains("hidden")) return false;

  const timer = deps.getTimer();
  if (!timer) return false;

  setText(doc, "#focusControllerTimer", deps.formatTimer(deps.getElapsedMs()));
  setText(doc, "#focusControllerPanelTimer", deps.formatTimer(deps.getElapsedMs()));
  setText(doc, "#focusControllerStatus", timer.running ? "Sessão em andamento" : "Sessão pausada");

  const pauseBtn = doc.querySelector("#focusControllerPauseButton");
  if (pauseBtn) {
    const label = timer.running ? "Pausar" : "Continuar";
    pauseBtn.textContent = label;
    pauseBtn.setAttribute("aria-label", label);
  }

  return true;
}

export function setupFocusController(options) {
  deps = {
    doc: options.doc || document,
    getTimer: options.getTimer || (() => null),
    getFocusOpen: options.getFocusOpen || (() => false),
    getViewId: options.getViewId || (() => "dashboard"),
    getColors: options.getColors || (() => ({})),
    formatTimer: options.formatTimer || ((ms) => String(ms)),
    getElapsedMs: options.getElapsedMs || (() => 0),
  };

  const doc = deps.doc;
  if (configuredDoc === doc) return; // ja configurado para este documento
  configuredDoc = doc;

  const toggle = doc.querySelector("#focusControllerToggle");
  if (toggle) toggle.addEventListener("click", toggleFocusController);

  // Click fora do controlador minimiza o painel expandido
  doc.addEventListener("click", (event) => {
    const root = doc.querySelector("#focusController");
    if (
      root &&
      root.classList.contains(FOCUS_CONTROLLER_EXPANDED_CLASS) &&
      !event.target.closest("#focusController")
    ) {
      collapseFocusController(doc);
    }
  });

  // Escape sempre minimiza o painel expandido
  doc.addEventListener("keydown", (event) => {
    if (event.key === "Escape") collapseFocusController(doc);
  });

  // Interacao dentro do controlador reinicia o auto-minimize
  doc.addEventListener("pointerdown", (event) => {
    if (event.target.closest("#focusController")) scheduleAutoMinimize();
  });
}
